import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { BarChart3, Crown, CreditCard, Target, Users, Plus, Clock, CheckCircle2, TrendingUp, Loader2 } from 'lucide-react';
import { useUserCognitiveData } from '@/hooks/useUserCognitiveData';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';
import MetricsOverview from '@/components/profile/MetricsOverview';
import QuickActionCards from '@/components/dashboard/QuickActionCards';
import { CHART_LINE_STYLES } from '@/lib/chart-constants';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/contexts/CompanyContext';
import { useCountUp } from '@/hooks/useCountUp';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface DashboardOverviewProps {
  totalCounts?: {
    behavioral: number;
    idealProfiles: number;
  };
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({
  totalCounts = { behavioral: 0, idealProfiles: 0 }
}) => {
  const { user, isAdmin } = useAuth();
  const { subscribed, subscriptionTier } = useSubscription();
  const { currentCompany } = useCompany();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Count-up animations for stat cards
  const { formattedValue: totalUsersCount } = useCountUp(totalCounts.behavioral, { duration: 1200 });
  const { formattedValue: idealProfilesCount } = useCountUp(totalCounts.idealProfiles, { duration: 1200 });

  // Fetch user's cognitive data
  const { cognitiveData, loadingCognitive } = useUserCognitiveData();
  
  // State for user's behavioral data
  const [userBehavioralData, setUserBehavioralData] = useState<any>(null);
  const [loadingBehavioral, setLoadingBehavioral] = useState(false);

  // Real chart data state
  const [chartData, setChartData] = useState<{ name: string; Assessments: number }[]>([]);
  const [loadingChart, setLoadingChart] = useState(false);

  // Actionable stats state
  const [newThisWeek, setNewThisWeek] = useState(0);
  const [awaitingReview, setAwaitingReview] = useState(0);
  const [profileMatches, setProfileMatches] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);

  const userName = user?.email?.split('@')[0] || 'User';

  // Fetch user's behavioral data
  useEffect(() => {
    const fetchUserBehavioralData = async () => {
      if (!user?.id) return;
      
      setLoadingBehavioral(true);
      try {
        const { data, error } = await supabase
          .from('survey_submissions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user behavioral data:', error);
        } else if (data) {
          setUserBehavioralData(data);
        }
      } catch (error) {
        console.error('Error fetching user behavioral data:', error);
      } finally {
        setLoadingBehavioral(false);
      }
    };

    fetchUserBehavioralData();
  }, [user?.id]);

  // Fetch real chart data — last 6 months of assessment completions
  useEffect(() => {
    const fetchChartData = async () => {
      if (!isAdmin()) return;
      setLoadingChart(true);
      try {
        const months = Array.from({ length: 6 }, (_, i) => {
          const d = subMonths(new Date(), 5 - i);
          return { label: format(d, 'MMM'), start: startOfMonth(d).toISOString(), end: endOfMonth(d).toISOString() };
        });

        const companyId = currentCompany?.company_id;

        const results = await Promise.all(
          months.map(async ({ label, start, end }) => {
            let query = supabase
              .from('survey_submissions')
              .select('id', { count: 'exact', head: true })
              .gte('created_at', start)
              .lte('created_at', end);
            if (companyId) query = query.eq('company_id', companyId);
            const { count } = await query;
            return { name: label, Assessments: count || 0 };
          })
        );
        setChartData(results);
      } catch (err) {
        setChartData([]);
      } finally {
        setLoadingChart(false);
      }
    };
    fetchChartData();
  }, [isAdmin, currentCompany?.company_id]);

  // Fetch actionable dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!isAdmin()) return;
      setLoadingStats(true);
      try {
        const companyId = currentCompany?.company_id;
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

        // New this week
        let weekQuery = supabase
          .from('survey_submissions')
          .select('id', { count: 'exact', head: true })
          .gte('created_at', oneWeekAgo);
        if (companyId) weekQuery = weekQuery.eq('company_id', companyId);
        const { count: weekCount } = await weekQuery;
        setNewThisWeek(weekCount || 0);

        // Awaiting review (status is null or 'Screening')
        let reviewQuery = supabase
          .from('survey_submissions')
          .select('id', { count: 'exact', head: true })
          .or('status.is.null,status.eq.Screening');
        if (companyId) reviewQuery = reviewQuery.eq('company_id', companyId);
        const { count: reviewCount } = await reviewQuery;
        setAwaitingReview(reviewCount || 0);

        // Profile matches — submissions with a profile_name assigned
        let matchQuery = supabase
          .from('survey_submissions')
          .select('id', { count: 'exact', head: true })
          .not('profile_name', 'is', null);
        if (companyId) matchQuery = matchQuery.eq('company_id', companyId);
        const { count: matchCount } = await matchQuery;
        setProfileMatches(matchCount || 0);
      } catch (err) {
        // silently fail — non-critical
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, [isAdmin, currentCompany?.company_id]);

  // Calculate behavioral scores if data exists
  const rawBehavioralScores = userBehavioralData ? calculateBehavioralScores(userBehavioralData.scores) : null;
  
  // Transform to MetricsOverview format
  const behavioralData = rawBehavioralScores ? {
    dominance: { score: rawBehavioralScores.aScore, percentile: rawBehavioralScores.aPercentile },
    extraversion: { score: rawBehavioralScores.bScore, percentile: rawBehavioralScores.bPercentile },
    patience: { score: rawBehavioralScores.cScore, percentile: rawBehavioralScores.cPercentile },
    formality: { score: rawBehavioralScores.dScore, percentile: rawBehavioralScores.dPercentile },
    objectivity: { score: rawBehavioralScores.eScore, percentile: rawBehavioralScores.ePercentile }
  } : null;

  return (
    <div className="p-8 space-y-8">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-1">
            Welcome back, {userName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin() ? 'Manage your platform and track user activity.' : 'Manage your assessments and track your progress.'}
          </p>
        </div>
        {subscribed && (
          <Badge className="text-xs px-3 py-1 bg-gradient-to-r from-primary to-accent text-white border-none shadow-lg">
            <Crown className="h-3 w-3 mr-1" />
            {subscriptionTier || 'Pro'}
          </Badge>
        )}
      </div>

      {/* Quick Action Cards — context-aware */}
      <QuickActionCards
        totalCounts={totalCounts}
        pendingCount={awaitingReview}
        hasIdealProfile={totalCounts.idealProfiles > 0}
      />

      {/* User Assessment Results */}
      {behavioralData && !isAdmin() && (
        <MetricsOverview
          behavioralData={behavioralData}
          cognitiveData={cognitiveData}
          loadingCognitive={loadingCognitive}
        />
      )}

      {/* Stats Cards — actionable signals for admins, simple totals for non-admins */}
      <div className={`grid gap-6 ${isMobile ? 'grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
        {/* Card 1: New This Week (admin) / Total Assessments (non-admin) */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 hover:border-primary/40 hover:shadow-xl transition-all duration-300 cursor-pointer group">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-lg bg-gradient-to-br from-primary to-accent shadow-lg">
                {isAdmin() ? <TrendingUp className="h-5 w-5 text-white" /> : <Users className="h-5 w-5 text-white" />}
              </div>
              {loadingStats && isAdmin() && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {isAdmin() ? 'New This Week' : 'Total Assessments'}
              </p>
              <div className="text-3xl font-bold text-foreground group-hover:scale-105 transition-transform">
                {isAdmin() ? newThisWeek : 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {isAdmin() ? `${totalCounts.behavioral} total candidates` : 'Completed assessments'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Awaiting Review (admin) / Ideal Profiles (admin) */}
        <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50/50 to-amber-50/50 dark:from-orange-950/30 dark:to-amber-950/30 dark:border-orange-800/30 hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-xl transition-all duration-300 cursor-pointer group"
          onClick={() => isAdmin() ? navigate('/dashboard?tab=behavioral') : undefined}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-lg bg-gradient-to-br from-orange-400 to-amber-500 shadow-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              {awaitingReview > 0 && isAdmin() && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-orange-500 rounded-full">!</span>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {isAdmin() ? 'Awaiting Review' : 'This Month'}
              </p>
              <div className="text-3xl font-bold text-foreground group-hover:scale-105 transition-transform">
                {isAdmin() ? awaitingReview : 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {isAdmin() ? 'Candidates in Screening' : 'Recent activity'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Profile Matches (admin) / Plan Status (non-admin) */}
        <Card className="border-emerald-200/50 bg-gradient-to-br from-emerald-50/50 to-green-50/50 dark:from-emerald-950/30 dark:to-green-950/30 dark:border-emerald-800/30 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-xl transition-all duration-300 cursor-pointer group">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {isAdmin() ? 'Profile Matches' : 'Plan Status'}
              </p>
              <div className="text-3xl font-bold text-foreground group-hover:scale-105 transition-transform">
                {isAdmin() ? profileMatches : (subscribed ? (subscriptionTier || 'Pro') : 'Free')}
              </div>
              <p className="text-xs text-muted-foreground">
                {isAdmin() ? 'Candidates with profile assigned' : (subscribed ? 'Active subscription' : 'Upgrade available')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Plan Status with Upgrade CTA */}
        <Card className={`hover:shadow-xl transition-all duration-300 cursor-pointer group ${
          subscribed
            ? 'border-secondary/20 bg-gradient-to-br from-secondary/5 to-purple-500/5 hover:border-secondary/40'
            : 'border-amber-200/50 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-800/30 hover:border-amber-300 dark:hover:border-amber-700'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-lg shadow-lg bg-gradient-to-br ${subscribed ? 'from-secondary to-purple-600' : 'from-amber-400 to-orange-500'}`}>
                {subscribed ? <Crown className="h-5 w-5 text-white" /> : <Target className="h-5 w-5 text-white" />}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {isAdmin() ? 'Active Profiles' : 'Your Plan'}
              </p>
              <div className="text-3xl font-bold text-foreground group-hover:scale-105 transition-transform">
                {isAdmin() ? idealProfilesCount : (subscribed ? (subscriptionTier || 'Pro') : 'Free')}
              </div>
              {!subscribed && !isAdmin() ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade Plan
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {isAdmin() ? 'Ideal profiles created' : 'Active subscription'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Chart — real Supabase data, hidden on mobile */}
      {!isMobile && isAdmin() && (
        <Card className="border-primary/20 bg-gradient-to-br from-background to-muted/30">
          <CardContent className="p-6">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Assessment Activity
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  New candidate assessments over the last 6 months
                </p>
              </div>
              {loadingChart && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mt-1" />}
            </div>
            {!loadingChart && chartData.every(d => d.Assessments === 0) ? (
              <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground">
                <BarChart3 className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm font-medium">No assessments yet</p>
                <p className="text-xs mt-1">Send your assessment link to start collecting data</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorAssessments" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray={CHART_LINE_STYLES.reference.strokeDasharray}
                    stroke="hsl(var(--border))"
                    className="opacity-50"
                  />
                  <XAxis
                    dataKey="name"
                    fontSize={12}
                    stroke="hsl(var(--muted-foreground))"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    fontSize={12}
                    stroke="hsl(var(--muted-foreground))"
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--background))',
                      border: '2px solid hsl(var(--primary))',
                      borderRadius: '8px',
                      fontSize: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      color: 'hsl(var(--foreground))'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="Assessments"
                    stroke="hsl(var(--primary))"
                    strokeWidth={CHART_LINE_STYLES.connecting.strokeWidth}
                    fill="url(#colorAssessments)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardOverview;
